import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Spin, Empty, Select, List, Button, Modal, message } from 'antd';
import { SwapOutlined, ArrowLeftOutlined, UndoOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { auth } from '@festpix/services';
import {
  getVendorBooth,
  getBoothTransactions,
  getBoothVendors,
  refundTransaction,
  Booth,
  BoothTransactionData,
} from '../../services/vendorService';
import VendorLayout from '../../components/VendorLayout';
import { colors, shadows } from '@festpix/ui';

const { Title, Text } = Typography;

const MAX_REFUNDABLE_TRANSACTIONS = 10;

export default function VendorTransactionsScreen() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [booth, setBooth] = useState<Booth | null>(null);
  const [transactions, setTransactions] = useState<BoothTransactionData[]>([]);
  const [vendors, setVendors] = useState<{ id: string; name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [transactionToRefund, setTransactionToRefund] = useState<BoothTransactionData | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      if (!user) return;

      const boothData = await getVendorBooth(user.uid);
      setBooth(boothData);

      if (boothData) {
        const [txData, vendorData] = await Promise.all([
          getBoothTransactions(boothData.id),
          getBoothVendors(boothData.id),
        ]);
        setTransactions(txData);
        setVendors(vendorData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    if (!selectedVendorId) return transactions;
    return transactions.filter((t) => t.performedBy?.id === selectedVendorId);
  }, [transactions, selectedVendorId]);

  const handleRefundClick = (transaction: BoothTransactionData) => {
    setTransactionToRefund(transaction);
    setConfirmModalVisible(true);
  };

  const handleConfirmRefund = async () => {
    if (!transactionToRefund || !user) return;

    setRefundingId(transactionToRefund.id);
    setConfirmModalVisible(false);

    try {
      await refundTransaction(transactionToRefund.id, user.uid);
      message.success('Estorno realizado com sucesso!');
      // Reload transactions
      if (booth) {
        const txData = await getBoothTransactions(booth.id);
        setTransactions(txData);
      }
    } catch (error: any) {
      console.error('Error refunding transaction:', error);
      message.error(error.message || 'Erro ao realizar estorno');
    } finally {
      setRefundingId(null);
      setTransactionToRefund(null);
    }
  };

  const formatDate = (timestamp: any) => {
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <VendorLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </VendorLayout>
    );
  }

  if (!booth) {
    return (
      <VendorLayout>
        <Card
          style={{
            borderRadius: 16,
            background: `${colors.warning}10`,
            border: `1px solid ${colors.warning}40`,
          }}
        >
          <Text style={{ color: colors.warning }}>
            Você ainda não está associado a nenhum estande. Entre em contato com o administrador.
          </Text>
        </Card>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout boothName={booth?.name}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/vendor')}
            style={{ padding: 0 }}
          />
          <Title level={3} style={{ margin: 0, flex: 1 }}>
            <SwapOutlined style={{ marginRight: 8 }} />
            Histórico de Vendas
          </Title>
        </div>

        {/* Vendor Filter */}
        {vendors.length > 1 && (
          <Card
            style={{
              borderRadius: 12,
              marginBottom: 16,
              boxShadow: shadows.sm,
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Filtrar por vendedor:
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Todos os vendedores"
              allowClear
              value={selectedVendorId}
              onChange={(value) => setSelectedVendorId(value || null)}
              options={[
                { value: '', label: 'Todos os vendedores' },
                ...vendors.map((v) => ({
                  value: v.id,
                  label: v.name,
                })),
              ]}
            />
          </Card>
        )}

        {/* Transactions List */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: shadows.md,
          }}
        >
          {filteredTransactions.length === 0 ? (
            <Empty description="Nenhuma transação encontrada" />
          ) : (
            <List
              dataSource={filteredTransactions}
              renderItem={(transaction, index) => {
                const isRefundable = index < MAX_REFUNDABLE_TRANSACTIONS && !transaction.refundedAt;
                const isRefunded = !!transaction.refundedAt;
                const isRefunding = refundingId === transaction.id;

                return (
                  <List.Item
                    style={{
                      padding: '16px 0',
                      borderBottom:
                        index < filteredTransactions.length - 1
                          ? `1px solid ${colors.border}`
                          : 'none',
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4,
                            }}
                          >
                            <UserOutlined style={{ color: colors.primary }} />
                            <Text strong style={{ fontSize: 15 }}>
                              {transaction.walletOwnerName}
                            </Text>
                          </div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatDate(transaction.timestamp)}
                          </Text>
                          {transaction.performedBy?.name && (
                            <Text
                              type="secondary"
                              style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                            >
                              Vendedor: {transaction.performedBy.name}
                            </Text>
                          )}
                        </div>
                        <Text
                          strong
                          style={{
                            fontSize: 16,
                            color: colors.success,
                          }}
                        >
                          R$ {transaction.amount.toFixed(2)}
                        </Text>
                      </div>

                      {/* Items breakdown if available */}
                      {transaction.items && transaction.items.length > 0 && (
                        <div
                          style={{
                            background: colors.backgroundSecondary,
                            borderRadius: 8,
                            padding: '8px 12px',
                            marginBottom: 8,
                          }}
                        >
                          {transaction.items.map((item, itemIndex) => (
                            <div
                              key={itemIndex}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 12,
                                color: colors.textSecondary,
                              }}
                            >
                              <span>
                                {item.quantity}x {item.shortName || item.name}
                              </span>
                              <span>R$ {item.totalAmount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Refunded indicator */}
                      {isRefunded && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 4,
                            padding: '4px 10px',
                            background: `${colors.warning}15`,
                            borderRadius: 6,
                            border: `1px solid ${colors.warning}40`,
                          }}
                        >
                          <CheckCircleOutlined style={{ color: colors.warning, fontSize: 14 }} />
                          <Text style={{ color: colors.warning, fontSize: 12, fontWeight: 500 }}>
                            Estornado
                          </Text>
                        </div>
                      )}

                      {/* Refund button for recent transactions (only if not already refunded) */}
                      {isRefundable && (
                        <Button
                          type="default"
                          danger
                          size="small"
                          icon={<UndoOutlined />}
                          loading={isRefunding}
                          onClick={() => handleRefundClick(transaction)}
                          style={{ marginTop: 4 }}
                        >
                          Estornar
                        </Button>
                      )}
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </Card>

        {/* Refund Confirmation Modal */}
        <Modal
          title="Confirmar Estorno"
          open={confirmModalVisible}
          onOk={handleConfirmRefund}
          onCancel={() => {
            setConfirmModalVisible(false);
            setTransactionToRefund(null);
          }}
          okText="Confirmar Estorno"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
        >
          {transactionToRefund && (
            <div>
              <p>
                Tem certeza que deseja estornar esta transação?
              </p>
              <Card
                size="small"
                style={{
                  background: colors.backgroundSecondary,
                  borderRadius: 8,
                }}
              >
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Cliente:</strong> {transactionToRefund.walletOwnerName}
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Valor:</strong>{' '}
                  <Text style={{ color: colors.error }}>
                    R$ {transactionToRefund.amount.toFixed(2)}
                  </Text>
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Data:</strong> {formatDate(transactionToRefund.timestamp)}
                </p>
              </Card>
              <p style={{ marginTop: 16, color: colors.textSecondary, fontSize: 13 }}>
                O valor será devolvido para a carteira do cliente e o estoque será restaurado (se aplicável).
              </p>
            </div>
          )}
        </Modal>
      </div>
    </VendorLayout>
  );
}
